import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchActorDetails, fetchActorMovies } from '../api/tmdbApi.js';

/**
 * Custom hook for managing actor details and movies
 * Handles actor data fetching, biography, movies, and pagination
 *
 * @param {Object} genresMap - Map of genre IDs to names
 * @param {Function} showSnackbar - Function to show notifications
 * @returns {Object} Actor state and methods
 */
const useActorDetails = (genresMap, showSnackbar) => {
  // Actor state
  const [selectedActor, setSelectedActor] = useState(null);
  const [actorBiography, setActorBiography] = useState('');
  const [actorMovies, setActorMovies] = useState([]);

  // Loading and error state
  const [isLoadingActorDetails, setIsLoadingActorDetails] = useState(false);
  const [isLoadingActorMovies, setIsLoadingActorMovies] = useState(false);
  const [actorDetailsError, setActorDetailsError] = useState('');

  // Pagination state
  const [actorMoviesCurrentPage, setActorMoviesCurrentPage] = useState(1);
  const [hasMoreActorMovies, setHasMoreActorMovies] = useState(true);

  // Refs
  const abortControllerRef = useRef(null);

  /**
   * Cancel any ongoing requests
   */
  const cancelPendingRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Cancel requests on unmount
  useEffect(() => {
    return () => {
      cancelPendingRequests();
    };
  }, [cancelPendingRequests]);

  /**
   * Fetch actor details and initial movies
   * @param {string|number} actorId - Actor ID to fetch
   */
  const fetchActorDetailsInternal = useCallback(async (actorId) => {
    if (!actorId) return;

    // Cancel any pending requests
    cancelPendingRequests();

    setIsLoadingActorDetails(true);
    setActorDetailsError('');
    setActorBiography('');
    setHasMoreActorMovies(true);

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Fetch actor details
    const { data: actorData, error: actorError } = await fetchActorDetails(actorId, signal);

    if (actorError) {
      setActorDetailsError(actorError);
      if (actorError !== 'Request cancelled') {
        showSnackbar(actorError, 'error');
      }
      setIsLoadingActorDetails(false);
      abortControllerRef.current = null;
      return;
    }

    setSelectedActor(actorData);
    setActorBiography(actorData.biography);
    setIsLoadingActorDetails(false);

    // Fetch first page of movies
    setIsLoadingActorMovies(true);

    const { data: moviesData, error: moviesError } = await fetchActorMovies({
      actorId,
      page: 1,
      genresMap,
      signal,
    });

    abortControllerRef.current = null;

    if (moviesError) {
      setActorDetailsError(moviesError);
      if (moviesError !== 'Request cancelled') {
        showSnackbar(moviesError, 'error');
      }
      setIsLoadingActorMovies(false);
      return;
    }

    setActorMovies(moviesData.movies);
    setActorMoviesCurrentPage(1);
    setHasMoreActorMovies(moviesData.hasMore);
    setIsLoadingActorMovies(false);
  }, [genresMap, showSnackbar, cancelPendingRequests]);

  /**
   * Load more actor movies (pagination)
   */
  const handleLoadMoreActorMovies = useCallback(async () => {
    if (!selectedActor || !hasMoreActorMovies || isLoadingActorMovies) {
      return;
    }

    setIsLoadingActorMovies(true);

    // Cancel any pending requests
    cancelPendingRequests();

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    const nextPage = actorMoviesCurrentPage + 1;

    const { data, error } = await fetchActorMovies({
      actorId: selectedActor.id,
      page: nextPage,
      genresMap,
      signal: abortControllerRef.current.signal,
    });

    abortControllerRef.current = null;

    if (error) {
      setActorDetailsError(error);
      if (error !== 'Request cancelled') {
        showSnackbar(error, 'error');
      }
      setIsLoadingActorMovies(false);
      return;
    }

    setActorMovies(prev => [...prev, ...data.movies]);
    setActorMoviesCurrentPage(nextPage);
    setHasMoreActorMovies(data.hasMore);
    setIsLoadingActorMovies(false);
  }, [
    selectedActor,
    actorMoviesCurrentPage,
    hasMoreActorMovies,
    isLoadingActorMovies,
    genresMap,
    showSnackbar,
    cancelPendingRequests,
  ]);

  /**
   * Clear actor details and reset state
   */
  const clearActorDetails = useCallback(() => {
    cancelPendingRequests();
    setSelectedActor(null);
    setActorBiography('');
    setActorMovies([]);
    setActorDetailsError('');
    setActorMoviesCurrentPage(1);
    setHasMoreActorMovies(true);
    setIsLoadingActorDetails(false);
    setIsLoadingActorMovies(false);
  }, [cancelPendingRequests]);

  /**
   * Clear actor error
   */
  const clearActorError = useCallback(() => {
    setActorDetailsError('');
  }, []);

  return {
    // Actor state
    selectedActor,
    actorBiography,
    actorMovies,

    // Loading and error state
    isLoadingActorDetails,
    isLoadingActorMovies,
    actorDetailsError,

    // Pagination state
    actorMoviesCurrentPage,
    hasMoreActorMovies,

    // Actions
    fetchActorDetails: fetchActorDetailsInternal,
    handleLoadMoreActorMovies,
    clearActorDetails,
    clearActorError,
    setSelectedActor,
  };
};

export default useActorDetails;
